import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { FlatService } from '../../../core/services';
import { CommonDropdownComponent } from '../../../core/ui/common-dropdown/common-dropdown.component';
import { DropdownInfoConfig, DropdownOptionLike } from '../../../core/constants/api-endpoints';
import { FLAT_PAY_DAY_MAX, FLAT_PAY_DAY_MIN } from '../../../core/constants/app-constants';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

@Component({
  selector: 'app-new-edit-flat',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonDropdownComponent, TranslatePipe],
  templateUrl: './new-edit-flat.component.html',
  styleUrls: ['./new-edit-flat.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class NewEditFlatComponent implements OnChanges {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialFlat: Record<string, any> | null = null;
  @Input() preselectedHouse: Record<string, any> | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  floorOptions: DropdownOptionLike[] = [];
  houseDropdownObj: Partial<DropdownInfoConfig> = {
    apiInfo: 'HOUSE_LIST',
    status: 1
  };
  floorDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'floorName',
    valueKey: 'value',
    display1: 'display',
    label: 'filter.floor',
    placeholder: 'placeholder.selectFloor',
    options: []
  };

  flat = {
    flatName: '',
    houseId: null as number | null,
    floorName: null as string | null,
    payDayTh: FLAT_PAY_DAY_MIN as number | null,
    rentAmount: null as number | null,
    paymentStyle: 'running' as 'after' | 'running',
    gasBillRequired: false,
    gasBillAmount: null as number | null,
    waterBillRequired: false,
    waterBillAmount: null as number | null,
    electricBillRequired: false,
    electricBillAmount: null as number | null,
    serviceChargeAndOtherRequired: false,
    serviceChargeAndOtherAmount: null as number | null,
    negativeChargeRequired: false,
    negativeChargeAmount: null as number | null,
    negativeChargeName: ''
  };

  isSubmitting = false;
  error: string | null = null;
  readonly payDayMin = FLAT_PAY_DAY_MIN;
  readonly payDayMax = FLAT_PAY_DAY_MAX;

  constructor(
    private readonly flats: FlatService
  ) {}

  ngOnInit(): void {
    if (this.mode !== 'edit' || !this.initialFlat) {
      this.applyPreselectedHouse();
      return;
    }
    this.prefillForm(this.initialFlat);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['preselectedHouse'] || changes['mode']) && this.mode === 'create' && !this.initialFlat) {
      this.applyPreselectedHouse();
    }
  }

  closeModal(): void {
    this.close.emit();
  }

  onHouseDropdownChange(event: { name: string; value: unknown; option?: DropdownOptionLike }): void {
    const houseId = Number(event.value);
    this.flat.houseId = Number.isFinite(houseId) ? houseId : null;

    const rawFloors = Array.isArray(event.option?.['floors'])
      ? (event.option?.['floors'] as unknown[])
      : [];

    this.floorOptions = rawFloors
      .map((item) => `${item ?? ''}`.trim())
      .filter((item) => item.length > 0)
      .map((item) => ({ value: item, display: item }));

    this.flat.floorName = null;
    this.floorDropdownObj = {
      ...this.floorDropdownObj,
      options: this.floorOptions
    };

    if (this.floorOptions.length === 1) {
      this.flat.floorName = `${this.floorOptions[0]['value']}`;
    }
  }

  onHouseOptionsLoaded(event: { name: string; options: DropdownOptionLike[] }): void {
    const houseOptions = Array.isArray(event.options) ? event.options : [];

    if (!this.flat.houseId) {
      this.tryApplyPreselectedHouseFromOptions(houseOptions);
      return;
    }

    const matchedHouse = houseOptions.find(
      (item: DropdownOptionLike) => Number(item?.['id']) === this.flat.houseId
    );
    const floors = Array.isArray(matchedHouse?.['floors']) ? (matchedHouse?.['floors'] as unknown[]) : [];
    this.setFloorOptionsFromRaw(floors);
  }

  onFloorDropdownChange(event: { name: string; value: unknown }): void {
    this.flat.floorName = event.value ? `${event.value}` : null;
  }

  increasePayDay(): void {
    this.flat.payDayTh = this.normalizePayDay((this.flat.payDayTh ?? this.payDayMin) + 1);
  }

  decreasePayDay(): void {
    this.flat.payDayTh = this.normalizePayDay((this.flat.payDayTh ?? this.payDayMin) - 1);
  }

  onPayDayInput(): void {
    this.flat.payDayTh = this.normalizePayDay(this.flat.payDayTh ?? this.payDayMin);
  }

  submitForm(): void {
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting = true;
    this.error = null;
    const request$ = this.mode === 'edit'
      ? this.flats.update(payload)
      : this.flats.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => this.save.emit(),
        error: (err: any) => {
          this.error = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} flat.`;
        }
      });
  }

  toggleBillAmount(field: 'gas' | 'water' | 'electric' | 'service' | 'other'): void {
    if (field === 'gas' && !this.flat.gasBillRequired) {
      this.flat.gasBillAmount = null;
    }
    if (field === 'water' && !this.flat.waterBillRequired) {
      this.flat.waterBillAmount = null;
    }
    if (field === 'electric' && !this.flat.electricBillRequired) {
      this.flat.electricBillAmount = null;
    }
    if (field === 'service' && !this.flat.serviceChargeAndOtherRequired) {
      this.flat.serviceChargeAndOtherAmount = null;
    }
    if (field === 'other' && !this.flat.negativeChargeRequired) {
      this.flat.negativeChargeAmount = null;
      this.flat.negativeChargeName = '';
    }
  }

  private buildPayload(): any | null {
    if (!this.flat.flatName.trim()) {
      this.error = 'Flat name is required.';
      return null;
    }
    if (!this.flat.houseId) {
      this.error = 'House is required.';
      return null;
    }
    if (!this.flat.floorName?.trim()) {
      this.error = 'Floor is required.';
      return null;
    }
    if (!this.flat.payDayTh || this.flat.payDayTh < this.payDayMin || this.flat.payDayTh > this.payDayMax) {
      this.error = `Pay day must be between ${this.payDayMin} and ${this.payDayMax}.`;
      return null;
    }
    if (!this.flat.rentAmount || this.flat.rentAmount < 0) {
      this.error = 'Rent amount is required.';
      return null;
    }
    if (this.flat.gasBillAmount !== null && this.flat.gasBillAmount < 0) {
      this.error = 'Gas bill amount cannot be negative.';
      return null;
    }
    if (this.flat.waterBillAmount !== null && this.flat.waterBillAmount < 0) {
      this.error = 'Water bill amount cannot be negative.';
      return null;
    }
    if (this.flat.electricBillAmount !== null && this.flat.electricBillAmount < 0) {
      this.error = 'Electric bill amount cannot be negative.';
      return null;
    }
    if (this.flat.serviceChargeAndOtherAmount !== null && this.flat.serviceChargeAndOtherAmount < 0) {
      this.error = 'Service charge + other amount cannot be negative.';
      return null;
    }
    if (this.flat.negativeChargeAmount !== null && this.flat.negativeChargeAmount < 0) {
      this.error = 'Negative charge amount cannot be negative.';
      return null;
    }
    if (this.flat.negativeChargeRequired && this.flat.negativeChargeName && this.flat.negativeChargeName.trim().length > 20) {
      this.error = 'Negative charge name max length is 20.';
      return null;
    }

    const payload: Record<string, any> = {
      flatName: this.flat.flatName.trim(),
      house: { id: this.flat.houseId },
      floorName: this.flat.floorName.trim(),
      payDayTh: this.flat.payDayTh,
      rentAmount: this.flat.rentAmount,
      gasBillAmount: this.flat.gasBillRequired ? this.flat.gasBillAmount ?? 0 : 0,
      electricBillAmount: this.flat.electricBillRequired ? this.flat.electricBillAmount ?? 0 : 0,
      waterBillAmount: this.flat.waterBillRequired ? this.flat.waterBillAmount ?? 0 : 0,
      serviceChargeAndOtherAmount: this.flat.serviceChargeAndOtherRequired ? this.flat.serviceChargeAndOtherAmount ?? 0 : 0,
      negativeChargeAmount: this.flat.negativeChargeRequired ? this.flat.negativeChargeAmount ?? 0 : 0,
      negativeChargeName: this.flat.negativeChargeRequired ? this.flat.negativeChargeName.trim().slice(0, 20) : '',
      status: 1,
      serviceChargeAndOtherRequired: this.flat.serviceChargeAndOtherRequired,
      waterBillRequired: this.flat.waterBillRequired,
      negativeChargeRequired: this.flat.negativeChargeRequired,
      currentMonthBilling: this.flat.paymentStyle === 'running',
      electricBillRequired: this.flat.electricBillRequired,
      gasBillRequired: this.flat.gasBillRequired
    };

    if (this.mode === 'edit') {
      const id = Number(this.initialFlat?.['id']);
      if (!Number.isFinite(id)) {
        this.error = 'Flat id is missing for update.';
        return null;
      }
      payload['id'] = id;
    }

    return payload;
  }

  private prefillForm(flat: Record<string, any>): void {
    this.flat.flatName = `${flat['flatName'] ?? ''}`;
    const houseId = Number(flat['house']?.['id']);
    this.flat.houseId = Number.isFinite(houseId) ? houseId : null;
    this.flat.floorName = flat['floorName'] ? `${flat['floorName']}` : null;
    this.flat.payDayTh = Number.isFinite(Number(flat['payDayTh']))
      ? this.normalizePayDay(Number(flat['payDayTh']))
      : this.payDayMin;
    this.flat.rentAmount = Number.isFinite(Number(flat['rentAmount'])) ? Number(flat['rentAmount']) : null;
    this.flat.paymentStyle = this.readBoolean(flat, ['isCurrentMonthBilling', 'currentMonthBilling'], true)
      ? 'running'
      : 'after';

    this.flat.gasBillRequired = this.readBoolean(flat, ['isGasBillRequired', 'gasBillRequired']);
    this.flat.waterBillRequired = this.readBoolean(flat, ['isWaterBillRequired', 'waterBillRequired']);
    this.flat.electricBillRequired = this.readBoolean(flat, ['isElectricBillRequired', 'electricBillRequired']);
    this.flat.serviceChargeAndOtherRequired = this.readBoolean(flat, ['isServiceChargeAndOtherRequired', 'serviceChargeAndOtherRequired']);
    this.flat.negativeChargeRequired = this.readBoolean(flat, ['isNegativeChargeRequired', 'negativeChargeRequired']);

    this.flat.gasBillAmount = this.flat.gasBillRequired ? Number(flat['gasBillAmount'] ?? 0) : null;
    this.flat.waterBillAmount = this.flat.waterBillRequired ? Number(flat['waterBillAmount'] ?? 0) : null;
    this.flat.electricBillAmount = this.flat.electricBillRequired ? Number(flat['electricBillAmount'] ?? 0) : null;
    this.flat.serviceChargeAndOtherAmount = this.flat.serviceChargeAndOtherRequired ? Number(flat['serviceChargeAndOtherAmount'] ?? 0) : null;
    this.flat.negativeChargeAmount = this.flat.negativeChargeRequired ? Number(flat['negativeChargeAmount'] ?? 0) : null;
    this.flat.negativeChargeName = this.flat.negativeChargeRequired ? `${flat['negativeChargeName'] ?? ''}` : '';

    const houseFloors = Array.isArray(flat['house']?.['floors']) ? flat['house']['floors'] : [];
    if (houseFloors.length > 0) {
      this.setFloorOptionsFromRaw(houseFloors);
      return;
    }

    if (!this.flat.houseId) {
      return;
    }
  }

  private setFloorOptionsFromRaw(rawFloors: unknown[]): void {
    this.floorOptions = rawFloors
      .map((item) => `${item ?? ''}`.trim())
      .filter((item) => item.length > 0)
      .map((item) => ({ value: item, display: item }));

    this.floorDropdownObj = {
      ...this.floorDropdownObj,
      options: this.floorOptions
    };

    if (!this.flat.floorName && this.floorOptions.length === 1) {
      this.flat.floorName = `${this.floorOptions[0]['value']}`;
    }
  }

  private readBoolean(source: Record<string, any>, keys: string[], defaultValue = false): boolean {
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        return Boolean(source[key]);
      }
    }
    return defaultValue;
  }

  private normalizePayDay(value: number): number {
    if (!Number.isFinite(value)) {
      return this.payDayMin;
    }
    const rounded = Math.trunc(value);
    return Math.min(this.payDayMax, Math.max(this.payDayMin, rounded));
  }

  private applyPreselectedHouse(): void {
    const houseId = Number(this.preselectedHouse?.['id']);
    if (!Number.isFinite(houseId)) {
      return;
    }

    this.flat.houseId = houseId;
    const floors = Array.isArray(this.preselectedHouse?.['floors']) ? (this.preselectedHouse?.['floors'] as unknown[]) : [];
    if (floors.length > 0) {
      this.setFloorOptionsFromRaw(floors);
    }
  }

  private tryApplyPreselectedHouseFromOptions(options: DropdownOptionLike[]): void {
    const houseId = Number(this.preselectedHouse?.['id']);
    if (!Number.isFinite(houseId)) {
      return;
    }

    const matchedHouse = options.find((item) => Number(item?.['id']) === houseId);
    if (!matchedHouse) {
      return;
    }

    this.flat.houseId = houseId;
    const floors = Array.isArray(matchedHouse?.['floors']) ? (matchedHouse['floors'] as unknown[]) : [];
    this.setFloorOptionsFromRaw(floors);
  }
}

